import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';

import { TopBar, Btn, IconButton, AtomBubble, WordRushSplash } from '../components/wr';
import { useAuth } from '../context/AuthContext';

/**
 * Welcome surface — two-path entry:
 *   1. Continue with Google (saves stats + history across devices)
 *   2. Just play (anonymous Firebase guest — full game, local profile)
 *
 * The previous "you can play as a guest from the home page" line was
 * misleading — there was no guest path; routes were gated on a real user.
 * Now signInAsGuest mints a real Firebase uid so socket auth + game
 * persistence work identically to a full account.
 */
export default function LoginScreen() {
  const navigate = useNavigate();
  const { signInWithGoogle, signIn, signInAsGuest } = useAuth();

  const [showEmail, setShowEmail] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<'google' | 'email' | 'guest' | null>(null);

  const handleGoogle = async () => {
    setError(null);
    setLoading('google');
    try {
      await signInWithGoogle();
      navigate('/home');
    } catch (e: any) {
      console.error(e);
      setError(e?.message || 'Sign-in failed. Try again?');
    } finally {
      setLoading(null);
    }
  };

  const handleGuest = async () => {
    setError(null);
    setLoading('guest');
    try {
      await signInAsGuest();
      navigate('/home');
    } catch (e: any) {
      console.error(e);
      setError(e?.message || "Guest sign-in failed — try Google sign-in instead?");
    } finally {
      setLoading(null);
    }
  };

  const handleEmail = async () => {
    if (!email || !password) return;
    setError(null);
    setLoading('email');
    try {
      await signIn(email, password);
      navigate('/home');
    } catch (e: any) {
      console.error(e);
      setError(e?.message || "We couldn't sign you in.");
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="app-shell auth-screen">
      <div className="screen-pad">
        <TopBar
          left={<IconButton icon="ChevronLeft" ariaLabel="Back" onClick={() => navigate('/')} />}
        />

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="wr-col gap-4"
          style={{ flex: 1 }}
        >
          <div className="wr-row wr-center" style={{ paddingTop: 24, paddingBottom: 12 }}>
            <WordRushSplash size="md" />
          </div>

          <div className="wr-col gap-2">
            <div className="wr-row wr-center gap-2">
              <Sparkles size={14} color="var(--accent)" />
              <span className="eyebrow accent">A word race for everyone</span>
            </div>
            <p className="wr-text-3" style={{ fontSize: 14, marginTop: 4, lineHeight: 1.4, textAlign: 'center', maxWidth: 320, marginLeft: 'auto', marginRight: 'auto' }}>
              Pick a letter, fill four categories, beat the clock. Sign in to save your games — or just play.
            </p>
          </div>

          <div className="wr-col gap-3 mt-3">
            <Btn kind="primary" size="xl" full icon="Globe" loading={loading === 'google'} onClick={handleGoogle}>
              Continue with Google
            </Btn>
            <Btn kind="dark" size="xl" full icon="Play" loading={loading === 'guest'} onClick={handleGuest}>
              Just play
            </Btn>
            <Btn kind="ghost" full icon="Mail" onClick={() => setShowEmail(s => !s)}>
              {showEmail ? 'Hide email sign-in' : 'Continue with email'}
            </Btn>

            {showEmail && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="card wr-col gap-3"
              >
                <div>
                  <div className="eyebrow" style={{ marginBottom: 6 }}>Email</div>
                  <div className="field">
                    <input
                      className="field-input"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="you@example.com"
                    />
                  </div>
                </div>
                <div>
                  <div className="eyebrow" style={{ marginBottom: 6 }}>Password</div>
                  <div className="field">
                    <input
                      className="field-input"
                      type="password"
                      autoComplete="current-password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="••••••••"
                    />
                  </div>
                </div>
                <Btn kind="primary" full icon="ArrowRight" loading={loading === 'email'} onClick={handleEmail}>
                  Sign in
                </Btn>
                <div className="wr-row" style={{ justifyContent: 'space-between', fontSize: 12 }}>
                  <Link to="/signup" style={{ color: 'var(--accent)', fontWeight: 600, textDecoration: 'none' }}>
                    Create account
                  </Link>
                  <Link to="/forgot-password" style={{ color: 'var(--ink-3)', fontWeight: 600, textDecoration: 'none' }}>
                    Forgot password?
                  </Link>
                </div>
              </motion.div>
            )}
          </div>

          {error && (
            <div className="card" style={{ background: 'var(--amber-soft)', boxShadow: 'none', padding: 12 }}>
              <div style={{ fontSize: 13, color: 'var(--amber)', fontWeight: 600 }}>{error}</div>
            </div>
          )}

          <div style={{ flex: 1 }} />
          <div style={{ paddingBottom: 16, fontSize: 11, color: 'var(--ink-3)', textAlign: 'center' }}>
            Guest accounts can be upgraded to full accounts later.
          </div>
        </motion.div>
      </div>
    </div>
  );
}
